import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link2, Loader2, X } from 'lucide-react'
import { Dialog, DialogContent, DialogClose } from '@meno/ui/components/ui/dialog'
import { Button } from '@meno/ui/components/ui/button'
import { Input } from '@meno/ui/components/ui/input'
import { Label } from '@meno/ui/components/ui/label'
import { Checkbox } from '@meno/ui/components/ui/checkbox'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@meno/ui/components/ui/select'
import { cn } from '@meno/ui/lib/utils'
import {
  createInteractiveMenu, updateInteractiveMenu, type QrPosition,
} from '@meno/ui/lib/api'

// ── Existing-link shape (subset of MenuOut used by this dialog) ────────────
export type InteractiveLink = {
  id: number
  name: string
  interactive_link: string
  show_qr?: boolean
  qr_position?: QrPosition
}

// Public QR endpoint — no JS dep. Switch to a self-hosted renderer if you
// need to render QRs offline or strip the third-party dependency.
function qrImageUrl(text: string, size = 160) {
  const safe = encodeURIComponent(text)
  return `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&margin=0&data=${safe}`
}

const QR_POSITION_CLASS: Record<QrPosition, string> = {
  'top-left':     'top-3 left-3',
  'top-right':    'top-3 right-3',
  'bottom-left':  'bottom-3 left-3',
  'bottom-right': 'bottom-3 right-3',
}

function nameFromUrl(raw: string): string {
  try {
    const u = new URL(/^https?:\/\//i.test(raw) ? raw : `https://${raw}`)
    return u.hostname.replace(/^www\./, '') || raw
  } catch {
    return raw
  }
}

// ── Component ──────────────────────────────────────────────────────────────
export default function CreateLinkDialog({
  open, onClose, existing, onSaved,
}: {
  open: boolean
  onClose: () => void
  existing?: InteractiveLink | null
  onSaved: (link: InteractiveLink) => void
}) {
  const { t } = useTranslation()
  const [url, setUrl]                 = useState(existing?.interactive_link ?? '')
  const [showQr, setShowQr]           = useState(existing?.show_qr ?? true)
  const [qrPosition, setQrPosition]   = useState<QrPosition>(existing?.qr_position ?? 'bottom-right')
  const [saving, setSaving]           = useState(false)

  // Reset state whenever the dialog opens with a different "existing" link.
  useEffect(() => {
    if (!open) return
    setUrl(existing?.interactive_link ?? '')
    setShowQr(existing?.show_qr ?? true)
    setQrPosition(existing?.qr_position ?? 'bottom-right')
  }, [open, existing])

  const finalUrl = useMemo(() => {
    const t = url.trim()
    if (!t) return ''
    return /^https?:\/\//i.test(t) ? t : `https://${t}`
  }, [url])

  const canSave = !!url.trim() && !saving

  const handleSave = async () => {
    if (!canSave) return
    setSaving(true)
    try {
      const name = nameFromUrl(finalUrl)
      if (existing) {
        const { data } = await updateInteractiveMenu(existing.id, {
          interactive_link: finalUrl,
          show_qr: showQr,
          qr_position: qrPosition,
        })
        onSaved({
          id: data.id, name: data.name, interactive_link: data.interactive_link,
          show_qr: data.show_qr, qr_position: data.qr_position,
        })
      } else {
        const { data } = await createInteractiveMenu(name, finalUrl, {
          show_qr: showQr, qr_position: qrPosition,
        })
        onSaved({
          id: data.id, name: data.name, interactive_link: data.interactive_link,
          show_qr: data.show_qr, qr_position: data.qr_position,
        })
      }
      onClose()
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose() }}>
      <DialogContent hideClose className="max-w-5xl p-0 overflow-hidden">
        {/* Single close X lives in the header — DialogContent's built-in
            corner X is suppressed via hideClose so only this one renders. */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="text-base font-bold text-foreground flex items-center gap-2">
            <Link2 className="w-4 h-4 text-primary" />
            {existing
              ? t('assets.editLinkTitle', 'Edit Webpage')
              : t('assets.createLinkTitle', 'Create New Webpage')}
          </h2>
          <DialogClose
            aria-label={t('common.close', 'Close')}
            className="w-7 h-7 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <X className="w-4 h-4" />
          </DialogClose>
        </div>

        {/* Body — form on the left, live preview on the right */}
        <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-border">
          {/* Form */}
          <div className="p-6 space-y-5">
            <div className="grid gap-2">
              <Label htmlFor="link-url">{t('assets.urlLabel', 'URL')}</Label>
              <Input
                id="link-url"
                autoFocus
                placeholder="https://example.com"
                value={url}
                onChange={e => setUrl(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && canSave) handleSave() }}
              />
            </div>

            <label className="flex items-center gap-3 cursor-pointer">
              <Checkbox checked={showQr} onCheckedChange={(v) => setShowQr(!!v)} />
              <span className="text-sm font-medium">
                {t('assets.displayQr', 'Display QR Code')}
              </span>
            </label>

            <div className={cn('grid grid-cols-[1fr_auto] items-center gap-3 transition-opacity', !showQr && 'opacity-50 pointer-events-none')}>
              <Label htmlFor="qr-pos" className="text-sm font-medium">
                {t('assets.qrPosition', 'QR Position')}
              </Label>
              <Select value={qrPosition} onValueChange={(v) => setQrPosition(v as QrPosition)}>
                <SelectTrigger id="qr-pos" className="w-40"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="top-left">{t('assets.qrTopLeft', 'Top Left')}</SelectItem>
                  <SelectItem value="top-right">{t('assets.qrTopRight', 'Top Right')}</SelectItem>
                  <SelectItem value="bottom-left">{t('assets.qrBottomLeft', 'Bottom Left')}</SelectItem>
                  <SelectItem value="bottom-right">{t('assets.qrBottomRight', 'Bottom Right')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="pt-2">
              <Button
                onClick={handleSave}
                disabled={!canSave}
                className="w-full h-11 gap-2"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {t('common.save', 'Save')}
              </Button>
            </div>
          </div>

          {/* Preview */}
          <div className="p-6 bg-muted/20">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
              {t('assets.preview', 'Preview')}
            </p>
            {finalUrl ? (
              <div className="relative aspect-video w-full rounded-xl overflow-hidden bg-white border border-border shadow-sm">
                {/* The iframe is best-effort — many sites block embedding via
                    X-Frame-Options / CSP. When that happens the screen will
                    still display the live page on the signage device; this
                    pane is only a preview for the admin. */}
                <iframe
                  src={finalUrl}
                  className="absolute inset-0 w-full h-full"
                  sandbox="allow-same-origin allow-scripts allow-popups"
                  title={t('assets.linkPreviewTitle', 'Link preview')}
                />
                {showQr && (
                  <div
                    className={cn(
                      'absolute z-10 bg-white p-1.5 rounded-md shadow-md border border-border',
                      QR_POSITION_CLASS[qrPosition],
                    )}
                  >
                    <img
                      src={qrImageUrl(finalUrl, 96)}
                      alt={t('assets.qrAlt', 'QR')}
                      className="block w-16 h-16"
                    />
                  </div>
                )}
              </div>
            ) : (
              <div className="aspect-video w-full rounded-xl border border-dashed border-border flex items-center justify-center text-sm text-muted-foreground">
                {t('assets.previewPlaceholder', 'Preview will appear here')}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
